import React from 'react';
import { Form, Input, Button, Tooltip } from 'antd';
import {
    PlusOutlined,
    MinusCircleOutlined,
    HolderOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    PointerSensor,
    DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { multilingualProfanityValidator } from '../../utils/multilingualProfanityValidator';

interface SortableFormListProps {
    name: string;
    label: string;
    placeholder?: string;
    fieldKey?: string;
    form: any;
    alternateName?: string;
    maxLength?: number;
    maxCount?: number;
    tooltipText?: string;
    extra?: string;
    disabled?: boolean;
}

const SortableItem: React.FC<{
    id: string;
    children: (props: { listeners: any }) => React.ReactNode;
}> = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        marginBottom: 8,
        padding: 8,
        borderRadius: 6,
        background: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        gap: 8
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            {children({ listeners })}
        </div>
    );
};

const SortableFormList: React.FC<SortableFormListProps> = ({
    name,
    label,
    placeholder = '',
    fieldKey = 'name',
    form,
    alternateName,
    maxLength,
    maxCount,
    tooltipText,
    extra,
    disabled = false
}) => {
    const sensors = useSensors(useSensor(PointerSensor));

    const labelWithInfo = (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {label}
            {tooltipText && (
                <Tooltip title={tooltipText}>
                    <InfoCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
            )}
        </span>
    );

    return (
        <Form.Item label={labelWithInfo} extra={extra} >
            <Form.List name={name}>
                {(fields, { add, remove, move }) => {
                    const ids = fields.map(field => field.key.toString());

                    const handleDragEnd = (event: DragEndEvent) => {
                        const { active, over } = event;
                        if (active.id && over?.id) {
                            const oldIndex = ids.indexOf(active.id.toString());
                            const newIndex = ids.indexOf(over.id.toString());
                            move(oldIndex, newIndex);
                        }
                    };

                    return (
                        <>
                            {fields.length === 0 && (
                                <div
                                    style={{
                                        marginBottom: 8,
                                        padding: '6px 12px',
                                        background: '#fafafa',
                                        border: '1px dashed #d9d9d9',
                                        borderRadius: 4,
                                        color: '#999',
                                        fontStyle: 'italic',
                                        textAlign: 'center'
                                    }}
                                >
                                    {`No ${label} added yet.`}
                                </div>
                            )}

                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                                    {fields.map(field => (
                                        <SortableItem key={field.key} id={field.key.toString()}>
                                            {({ listeners }) => (
                                                <>
                                                    <Tooltip title="Drag to reorder">
                                                        <HolderOutlined
                                                            {...listeners}
                                                            style={{ cursor: 'grab', color: '#999' }}
                                                        />
                                                    </Tooltip>
                                                    <Form.Item
                                                        {...field}
                                                        name={[field.name, fieldKey]}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: `${alternateName?.toLowerCase() || label.toLowerCase()} name is required`
                                                            },
                                                            {
                                                                validator: multilingualProfanityValidator(
                                                                    'english',
                                                                    'Inappropriate language detected'
                                                                )
                                                            },
                                                            ...(maxLength
                                                                ? [
                                                                    {
                                                                        max: maxLength,
                                                                        message: `${label} must be at most ${maxLength} characters`
                                                                    }
                                                                ]
                                                                : [])
                                                        ]}
                                                        style={{ marginBottom: 0, flex: 1 }}
                                                    >
                                                        <Input
                                                            placeholder={placeholder}
                                                            size="small"
                                                            spellCheck={true}
                                                            maxLength={maxLength}
                                                            showCount={!!maxLength}
                                                            style={{ width: '100%' }}
                                                            disabled={disabled}
                                                        />
                                                    </Form.Item>
                                                    <Tooltip title="Remove">
                                                        <MinusCircleOutlined
                                                            onClick={() => remove(field.name)}
                                                            style={{ color: '#ff4d4f', cursor: 'pointer' }}
                                                        />
                                                    </Tooltip>
                                                </>
                                            )}
                                        </SortableItem>
                                    ))}
                                </SortableContext>
                            </DndContext>

                            {(!maxCount || fields.length < maxCount) && (
                                <Form.Item style={{ marginTop: 8 }}>
                                    <Button
                                        type="dashed"
                                        onClick={() => add()}
                                        icon={<PlusOutlined />}
                                        size="small"
                                        block
                                        disabled={disabled}
                                    >
                                        {alternateName ? `Add New ${alternateName}` : `Add New ${label}`}
                                    </Button>
                                </Form.Item>
                            )}
                        </>
                    );
                }}
            </Form.List>
        </Form.Item>
    );
};

export default SortableFormList;